import printJS from "print-js";
import { documentService } from "@miplatform/api";
import {
  FILE_MIME_TYPE_EXTENSIONS,
  FILE_TYPES,
  HTTP_STATUSES,
  IMAGE_FILE_TYPES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { getRootUrl } from "@miplatform/utils";

export const useDocumentsViewModel = () => {
  const stateManager = useComponentState();

  /**
   * Uploads a document.
   *
   * @param {Object} params - The parameters for uploading the document.
   * @param {File} params.file - The file to be uploaded.
   * @param {string} params.documentType - Find it from DOCUMENT_TYPES in the constants. Responsible for which container does the document get stored in the blob. For example, DOCUMENT_TYPES.IMAGE gets stored in blob/images, and DOCUMENT_TYPES.UNKNOWN gets stored in blob/other.
   * @param {string} params.externalReferenceId - The external reference ID associated with the document. Find it from the RefId in the table of entity that you are uploading the document to.
   * @param {Object} [params.externalData] - The category of the document (optional).
   * @returns {Promise<Object|null>} The uploaded document data if successful, otherwise null.
   */
  const uploadDocument = ({
    file,
    documentType,
    category = null,
    externalReferenceId,
    externalData,
    additionalExternalReferences,
  }) => {
    return stateManager.run(async () => {
      const params = {
        title: file.name,
        documentType,
        externalReferenceId,
        externalData,
        category,
        additionalExternalReferences,
      };
      const response = await documentService.documents.create({
        data: file,
        params,
        contentType: file.type,
      });
      if (response.status === HTTP_STATUSES.OK) {
        return response.data;
      }
      return null;
    });
  };

  /**
   * Removes a document.
   *
   * @param {Object} params - The parameters for removing the document.
   * @param {string} params.id - The ID of the document to be removed.
   * @param {string} params.modifiedOn - The modification date of the document.
   * @returns {Promise<boolean>} True if the document was successfully removed, otherwise false.
   */
  const removeDocument = ({ id, modifiedOn }) => {
    return stateManager.run(async () => {
      const response = await documentService.documents.delete({
        id,
        modifiedOn,
      });
      return response.status === HTTP_STATUSES.OK;
    });
  };
  const getDeleteDocumentOptions = ({ id, modifiedOn }) => {
    return documentService.documents.getDeleteDocumentOptions({
      id,
      modifiedOn,
    });
  };
  const getDocumentCategories = async (entityType) => {
    return stateManager.run(async () => {
      const response =
        await documentService.documentCategories.getByDependentType({
          entityType,
        });

      if (response.status === HTTP_STATUSES.OK) {
        return response.data;
      }
      return null;
    });
  };

  const printFile = async (file) => {
    if (!file || file.error) return;

    const documentUrl = `${getRootUrl()}/${file.referenceId}`;
    const fileType = FILE_MIME_TYPE_EXTENSIONS[file.contentType]?.toUpperCase();

    if ([FILE_TYPES.XLSX, FILE_TYPES.CSV, FILE_TYPES.DOCX].includes(fileType)) {
      window.open(documentUrl, "_blank");
      return;
    }

    if (IMAGE_FILE_TYPES.includes(fileType)) {
      printJS({
        printable: documentUrl,
        type: "image",
        imageStyle: "width:100%;",
      });
    } else if (fileType === FILE_TYPES.PDF) {
      try {
        printJS(documentUrl);
      } catch (error) {
        console.error("PDF print error:", error);
      }
    } else {
      toastService.error({
        text: "Print Document Failed",
        subtext: "Try downloading the document instead.",
      });
    }
  };

  const downloadFile = async (file) => {
    try {
      const { name } = file;
      const url = `${getRootUrl()}/${file?.referenceId}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  return {
    loading: stateManager.isBusy(),
    uploadDocument,
    removeDocument,
    getDeleteDocumentOptions,
    getDocumentCategories,
    downloadFile,
    printFile,
  };
};
